import { Button } from '@/components/shadcn/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/shadcn/dropdown-menu'
import { IconMenuOutline } from '@icons/IconMenuOutline'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { useState } from 'react'
import { OverlayDrawerComponent } from '@/ui-legacy/OverlayDrawerComponent/OverlayDrawerComponent'
import { IconPersonOutline } from '@icons/IconPersonOutline'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { toast } from 'sonner'

type MessagesPlatformPlatformUserWithAudit = RestSchemas['Messages.Platform.PlatformUserWithAudit']

function ViewUserProjects({ userId }: { userId: number }) {
  // const queryClient = useQueryClient()
  const { getSubscriptionsApiGetOrganisationUser, getSupportApiGetUserProjects } = useRestClient()

  const { data: user } = getSubscriptionsApiGetOrganisationUser.useQuery({ query: { userId: userId } })
  const { data: projects } = getSupportApiGetUserProjects.useQuery({ query: { userId: userId } })

  return (
    <div className="p-4">
      {/* User Info */}
      <div className="mb-4 flex items-center">
        <IconPersonOutline className="mr-2 text-gray-500" size={20} />
        <div className="text-lg font-semibold">
          {user?.givenName} {user?.familyName}
        </div>
        <div className="ml-2 text-sm text-gray-600">({user?.email})</div>
      </div>

      {/* Projects List */}
      <div className="w-full max-w-full space-y-4">
        {projects?.map((x) => {
          return (
            <div key={x.projectName} className="rounded-lg border border-gray-300 bg-background p-4 shadow-sm">
              {x.projectName}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export function UserActionsMenu({ user }: { user: MessagesPlatformPlatformUserWithAudit }) {
  //const navigate = useNavigate()
  const [openSheet, setOpenSheet] = useState<'viewuser' | undefined>()
  const [confirmDialog, setConfirmDialog] = useState<'delete' | 'freeze' | 'endsession' | undefined>()

  const {
    deleteSubscriptionsApiDeleteUser,
    postSubscriptionsApiFreezeUser,
    postAuthenticationApiLogoutUser,
    getSupportApiGetPlatformUsers,
  } = useRestClient()

  const { mutate: deleteUser } = deleteSubscriptionsApiDeleteUser.useMutation(
    { query: { userId: user.id } }, // The path parameter
    {
      onSuccess: () => {
        toast.success('User disabled successfully.')
        onMutationSuccess()
      },
      onError: () => {
        toast.error('Failed to disable user.')
      },
    }
  )

  const { mutate: freezeUser } = postSubscriptionsApiFreezeUser.useMutation(
    { query: { userId: user.id } }, // The path parameter
    {
      onSuccess: () => {
        toast.success('User frozen successfully.')
        onMutationSuccess()
      },
      onError: () => {
        toast.error('Failed to freeze user.')
      },
    }
  )

  const { mutate: endUserSession } = postAuthenticationApiLogoutUser.useMutation(
    { path: { userId: user.id } }, // The path parameter
    {
      onSuccess: () => {
        toast.success('User session ended successfully.')
        onMutationSuccess()
      },
      onError: () => {
        toast.error('Failed to end user session.')
      },
    }
  )

  const onMutationSuccess = () => getSupportApiGetPlatformUsers.invalidateQueries()

  return (
    <>
      {/* <SheetWithFormComponent
        title={'View User'}
        description={''}
        // trigger={<DropdownMenuItem onSelect={() => {}}>View User</DropdownMenuItem>}
        triggerTooltip={'View the user'}
        wide
        isOpen={openSheet === 'viewuser'}
        onOpenChange={() => setOpenSheet(undefined)}
      > */}

      <OverlayDrawerComponent
        open={openSheet === 'viewuser'}
        setOpen={() => setOpenSheet(undefined)}
        title="View Projects"
        content={<ViewUserProjects userId={user.id}></ViewUserProjects>}
      />

      <ConfirmDialog
        isOpen={confirmDialog == 'delete'}
        title="Delete user"
        description="Please confirm that the user should be removed from the organisation?"
        onConfirm={() => deleteUser()}
        onOpenChange={(open) => !open && setConfirmDialog(undefined)}
      />

      <ConfirmDialog
        isOpen={confirmDialog == 'freeze'}
        title="Freeze user"
        description="Please confirm that the user should be frozen for the organisation?"
        onConfirm={() => freezeUser()}
        onOpenChange={(open) => !open && setConfirmDialog(undefined)}
      />

      <ConfirmDialog
        isOpen={confirmDialog == 'endsession'}
        title="End user session"
        description="Please confirm ending the user's session?"
        onConfirm={() => endUserSession()}
        onOpenChange={(open) => !open && setConfirmDialog(undefined)}
      />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" className="size-8 rounded-full">
            <IconMenuOutline size={16} />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          onCloseAutoFocus={(e) => {
            // Prevent focus returning to trigger which can close the menu
            e.preventDefault()
          }}
        >
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setOpenSheet('viewuser')}>View Projects</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setConfirmDialog('delete')}>Delete user</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setConfirmDialog('freeze')}>Freeze user</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setConfirmDialog('endsession')}>End user session</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}
